import type { LanguageCode, TranslationNamespace } from '@/types/i18n';

const CACHE_SCHEMA_VERSION = 2;
const CACHE_PREFIX = 'pf2e_locale_cache_v2:';
const CACHE_INDEX_KEY = `${CACHE_PREFIX}index`;

export const DEFAULT_LOCALE_CACHE_LIMITS = {
  maxEntries: 2,
  maxBytes: 4_000_000,
} as const;

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'key'> & {
  readonly length: number;
};

export type LocaleCacheStatus =
  | 'hit'
  | 'miss'
  | 'stored'
  | 'unavailable'
  | 'quota'
  | 'corrupt';

export type LocaleCacheReadResult =
  | {
    status: 'hit';
    language: LanguageCode;
    version: string;
    translations: TranslationNamespace;
    checksum: string;
    bytes: number;
  }
  | { status: 'miss'; reason: 'empty' | 'stale' | 'evicted' }
  | { status: 'unavailable'; operation: string }
  | { status: 'corrupt'; reason: string };

export type LocaleCacheWriteResult =
  | { status: 'stored'; checksum: string; bytes: number }
  | { status: 'unavailable'; operation: string }
  | { status: 'quota'; operation: string }
  | { status: 'corrupt'; reason: string };

export type LocaleCacheClearResult =
  | { status: 'stored'; removed: number }
  | { status: 'unavailable'; operation: string };

interface LocaleCacheEnvelope {
  schemaVersion: typeof CACHE_SCHEMA_VERSION;
  language: LanguageCode;
  version: string;
  checksum: string;
  translations: TranslationNamespace;
}

interface LocaleCachePointer {
  schemaVersion: typeof CACHE_SCHEMA_VERSION;
  language: LanguageCode;
  version: string;
  entryKey: string;
  checksum: string;
  bytes: number;
}

interface LocaleCacheIndexEntry extends LocaleCachePointer {
  accessedAt: number;
}

interface LocaleCacheIndex {
  schemaVersion: typeof CACHE_SCHEMA_VERSION;
  entries: LocaleCacheIndexEntry[];
}

interface LocaleCacheLimits {
  maxEntries: number;
  maxBytes: number;
}

export interface LocaleCacheAdapter {
  read: (language: LanguageCode, version?: string) => LocaleCacheReadResult;
  write: (
    language: LanguageCode,
    version: string,
    translations: TranslationNamespace,
  ) => LocaleCacheWriteResult;
  protect: (language: LanguageCode | null, version: string | null) => void;
  clear: () => LocaleCacheClearResult;
}

function latestPointerKey(language: LanguageCode): string {
  return `${CACHE_PREFIX}latest:${language}`;
}

function storageBytes(value: string): number {
  // DOM Storage quotas are commonly accounted in UTF-16 code units. Using
  // two bytes per code unit deliberately overestimates ASCII-heavy payloads.
  return value.length * 2;
}

function payloadChecksum(serializedPayload: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < serializedPayload.length; index += 1) {
    hash ^= serializedPayload.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return `fnv1a32:${(hash >>> 0).toString(16).padStart(8, '0')}:${serializedPayload.length}`;
}

function isTranslationNamespace(value: unknown): value is TranslationNamespace {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;

  const pending: unknown[] = [value];
  const visitedObjects = new WeakSet<object>();
  let visited = 0;
  while (pending.length > 0) {
    const current = pending.pop();
    if (!current || typeof current !== 'object' || Array.isArray(current)) return false;
    if (visitedObjects.has(current)) return false;
    visitedObjects.add(current);
    visited += 1;
    if (visited > 100_000) return false;

    for (const child of Object.values(current)) {
      if (typeof child === 'string') continue;
      if (!child || typeof child !== 'object' || Array.isArray(child)) return false;
      pending.push(child);
    }
  }
  return true;
}

function isQuotaError(error: unknown): boolean {
  if (!(error instanceof Error) && (typeof error !== 'object' || error === null)) return false;
  const candidate = error as { name?: string; code?: number };
  return candidate.name === 'QuotaExceededError'
    || candidate.name === 'NS_ERROR_DOM_QUOTA_REACHED'
    || candidate.code === 22
    || candidate.code === 1014;
}

function parsePointer(raw: string): LocaleCachePointer | null {
  try {
    const pointer = JSON.parse(raw) as Partial<LocaleCachePointer>;
    if (
      pointer.schemaVersion !== CACHE_SCHEMA_VERSION
      || typeof pointer.language !== 'string'
      || typeof pointer.version !== 'string'
      || typeof pointer.entryKey !== 'string'
      || !pointer.entryKey.startsWith(CACHE_PREFIX)
      || typeof pointer.checksum !== 'string'
      || typeof pointer.bytes !== 'number'
      || !Number.isFinite(pointer.bytes)
      || pointer.bytes <= 0
    ) {
      return null;
    }
    return pointer as LocaleCachePointer;
  } catch {
    return null;
  }
}

function parseIndex(raw: string | null): LocaleCacheIndexEntry[] {
  if (!raw) return [];
  try {
    const index = JSON.parse(raw) as Partial<LocaleCacheIndex>;
    if (index.schemaVersion !== CACHE_SCHEMA_VERSION || !Array.isArray(index.entries)) {
      return [];
    }
    return index.entries.filter(entry => (
      parsePointer(JSON.stringify(entry)) !== null
      && typeof entry.accessedAt === 'number'
      && Number.isFinite(entry.accessedAt)
    ));
  } catch {
    return [];
  }
}

function parseEnvelope(
  raw: string,
  pointer: LocaleCachePointer,
): LocaleCacheEnvelope | null {
  try {
    const envelope = JSON.parse(raw) as Partial<LocaleCacheEnvelope>;
    if (
      envelope.schemaVersion !== CACHE_SCHEMA_VERSION
      || envelope.language !== pointer.language
      || envelope.version !== pointer.version
      || envelope.checksum !== pointer.checksum
      || !isTranslationNamespace(envelope.translations)
      || storageBytes(raw) !== pointer.bytes
    ) {
      return null;
    }
    const serializedPayload = JSON.stringify(envelope.translations);
    if (payloadChecksum(serializedPayload) !== envelope.checksum) return null;
    return envelope as LocaleCacheEnvelope;
  } catch {
    return null;
  }
}

export function createLocaleCacheAdapter(
  storageFactory: () => StorageLike,
  limits: LocaleCacheLimits = DEFAULT_LOCALE_CACHE_LIMITS,
): LocaleCacheAdapter {
  let protectedIdentity: { language: LanguageCode; version: string } | null = null;

  const acquireStorage = (): StorageLike | null => {
    try {
      const storage = storageFactory();
      void storage.length;
      return storage;
    } catch {
      return null;
    }
  };

  const removeBestEffort = (storage: StorageLike, key: string): void => {
    try {
      storage.removeItem(key);
    } catch {
      // Cache cleanup must never affect locale activation.
    }
  };

  const readRaw = (
    storage: StorageLike,
    key: string,
  ): { ok: true; value: string | null } | { ok: false } => {
    try {
      return { ok: true, value: storage.getItem(key) };
    } catch {
      return { ok: false };
    }
  };

  const writeRaw = (
    storage: StorageLike,
    key: string,
    value: string,
  ): 'stored' | 'unavailable' | 'quota' => {
    try {
      storage.setItem(key, value);
      return 'stored';
    } catch (error) {
      return isQuotaError(error) ? 'quota' : 'unavailable';
    }
  };

  const readIndex = (storage: StorageLike): LocaleCacheIndexEntry[] => {
    const result = readRaw(storage, CACHE_INDEX_KEY);
    return result.ok ? parseIndex(result.value) : [];
  };

  const writeIndex = (
    storage: StorageLike,
    entries: LocaleCacheIndexEntry[],
  ): 'stored' | 'unavailable' | 'quota' => writeRaw(storage, CACHE_INDEX_KEY, JSON.stringify({
    schemaVersion: CACHE_SCHEMA_VERSION,
    entries,
  } satisfies LocaleCacheIndex));

  const removePointerForEntry = (
    storage: StorageLike,
    entry: LocaleCacheIndexEntry,
  ): void => {
    const pointerKey = latestPointerKey(entry.language);
    const pointerResult = readRaw(storage, pointerKey);
    if (!pointerResult.ok || !pointerResult.value) return;
    const pointer = parsePointer(pointerResult.value);
    if (pointer?.entryKey === entry.entryKey) {
      removeBestEffort(storage, pointerKey);
    }
  };

  const removeEntry = (storage: StorageLike, entry: LocaleCacheIndexEntry): void => {
    removeBestEffort(storage, entry.entryKey);
    removePointerForEntry(storage, entry);
  };

  const findProtectedEntryKey = (entries: LocaleCacheIndexEntry[]): string | null => {
    if (!protectedIdentity) return null;
    return entries.find(entry => (
      entry.language === protectedIdentity?.language
      && entry.version === protectedIdentity.version
    ))?.entryKey ?? null;
  };

  const compactEntries = (
    storage: StorageLike,
    entries: LocaleCacheIndexEntry[],
    incomingBytes: number,
    protectedKeys: Set<string>,
    temporaryExtraEntry: boolean,
  ): LocaleCacheIndexEntry[] | null => {
    const result = [...entries];
    const targetCount = limits.maxEntries + (temporaryExtraEntry ? 1 : 0);
    const totalBytes = () => result.reduce((sum, entry) => sum + entry.bytes, 0);

    while (
      result.length + (temporaryExtraEntry ? 1 : 0) > targetCount
      || totalBytes() + incomingBytes > limits.maxBytes
    ) {
      const candidate = result
        .filter(entry => !protectedKeys.has(entry.entryKey))
        .sort((left, right) => left.accessedAt - right.accessedAt)[0];
      if (!candidate) return null;
      removeEntry(storage, candidate);
      result.splice(result.findIndex(entry => entry.entryKey === candidate.entryKey), 1);
    }
    return result;
  };

  const read = (language: LanguageCode, version?: string): LocaleCacheReadResult => {
    const storage = acquireStorage();
    if (!storage) return { status: 'unavailable', operation: 'acquire' };

    const pointerResult = readRaw(storage, latestPointerKey(language));
    if (!pointerResult.ok) return { status: 'unavailable', operation: 'getItem' };
    if (!pointerResult.value) return { status: 'miss', reason: 'empty' };

    let pointer = parsePointer(pointerResult.value);
    let usingLatestPointer = true;
    if (!pointer || pointer.language !== language) {
      removeBestEffort(storage, latestPointerKey(language));
      return { status: 'corrupt', reason: 'invalid_pointer' };
    }

    if (version && pointer.version !== version) {
      const exactEntry = readIndex(storage).find(entry => (
        entry.language === language && entry.version === version
      ));
      if (!exactEntry) return { status: 'miss', reason: 'stale' };
      pointer = exactEntry;
      usingLatestPointer = false;
    }

    const entryResult = readRaw(storage, pointer.entryKey);
    if (!entryResult.ok) return { status: 'unavailable', operation: 'getItem' };
    if (!entryResult.value) {
      if (usingLatestPointer) removeBestEffort(storage, latestPointerKey(language));
      const remaining = readIndex(storage).filter(entry => entry.entryKey !== pointer.entryKey);
      writeIndex(storage, remaining);
      return { status: 'miss', reason: 'evicted' };
    }

    const envelope = parseEnvelope(entryResult.value, pointer);
    if (!envelope) {
      removeBestEffort(storage, pointer.entryKey);
      if (usingLatestPointer) removeBestEffort(storage, latestPointerKey(language));
      const remaining = readIndex(storage).filter(entry => entry.entryKey !== pointer.entryKey);
      writeIndex(storage, remaining);
      return { status: 'corrupt', reason: 'invalid_entry' };
    }

    const entries = readIndex(storage);
    const touched = entries.map(entry => entry.entryKey === pointer.entryKey
      ? { ...entry, accessedAt: Date.now() }
      : entry);
    writeIndex(storage, touched);

    return {
      status: 'hit',
      language: envelope.language,
      version: envelope.version,
      translations: envelope.translations,
      checksum: envelope.checksum,
      bytes: pointer.bytes,
    };
  };

  const write = (
    language: LanguageCode,
    version: string,
    translations: TranslationNamespace,
  ): LocaleCacheWriteResult => {
    if (!version || !isTranslationNamespace(translations)) {
      return { status: 'corrupt', reason: 'invalid_payload' };
    }

    const serializedPayload = JSON.stringify(translations);
    const checksum = payloadChecksum(serializedPayload);
    const envelope: LocaleCacheEnvelope = {
      schemaVersion: CACHE_SCHEMA_VERSION,
      language,
      version,
      checksum,
      translations,
    };
    const serializedEnvelope = JSON.stringify(envelope);
    const bytes = storageBytes(serializedEnvelope);
    if (bytes > limits.maxBytes) return { status: 'quota', operation: 'entry_too_large' };

    const storage = acquireStorage();
    if (!storage) return { status: 'unavailable', operation: 'acquire' };

    const entryKey = `${CACHE_PREFIX}entry:${language}:${encodeURIComponent(version)}:${checksum}`;
    const oldPointerResult = readRaw(storage, latestPointerKey(language));
    if (!oldPointerResult.ok) return { status: 'unavailable', operation: 'getItem' };
    const oldPointer = oldPointerResult.value ? parsePointer(oldPointerResult.value) : null;
    const originalEntries = readIndex(storage);
    const protectedKeys = new Set<string>();
    const activeEntryKey = findProtectedEntryKey(originalEntries);
    if (activeEntryKey) protectedKeys.add(activeEntryKey);
    if (oldPointer) protectedKeys.add(oldPointer.entryKey);
    protectedKeys.add(entryKey);

    const entriesWithoutIncoming = originalEntries.filter(entry => entry.entryKey !== entryKey);
    let compacted = compactEntries(
      storage,
      entriesWithoutIncoming,
      bytes,
      protectedKeys,
      true,
    );
    if (!compacted) return { status: 'quota', operation: 'protected_budget' };

    const existingEntry = readRaw(storage, entryKey);
    if (!existingEntry.ok) return { status: 'unavailable', operation: 'getItem:entry' };
    let entryWrite = writeRaw(storage, entryKey, serializedEnvelope);
    while (entryWrite === 'quota') {
      const recoveryCandidate: LocaleCacheIndexEntry | undefined = compacted
        .filter(entry => !protectedKeys.has(entry.entryKey))
        .sort((left, right) => left.accessedAt - right.accessedAt)[0];
      if (!recoveryCandidate) break;
      removeEntry(storage, recoveryCandidate);
      compacted = compacted.filter(entry => entry.entryKey !== recoveryCandidate.entryKey);
      writeIndex(storage, compacted);
      entryWrite = writeRaw(storage, entryKey, serializedEnvelope);
    }
    if (entryWrite !== 'stored') return { status: entryWrite, operation: 'setItem:entry' };

    const verification = readRaw(storage, entryKey);
    const pointer: LocaleCachePointer = {
      schemaVersion: CACHE_SCHEMA_VERSION,
      language,
      version,
      entryKey,
      checksum,
      bytes,
    };
    if (!verification.ok || !verification.value || !parseEnvelope(verification.value, pointer)) {
      if (!existingEntry.value) removeBestEffort(storage, entryKey);
      return verification.ok
        ? { status: 'corrupt', reason: 'write_verification_failed' }
        : { status: 'unavailable', operation: 'getItem:verify' };
    }

    const nextEntry: LocaleCacheIndexEntry = { ...pointer, accessedAt: Date.now() };
    const nextEntries = [...compacted, nextEntry];
    const indexWrite = writeIndex(storage, nextEntries);
    if (indexWrite !== 'stored') {
      if (!existingEntry.value) removeBestEffort(storage, entryKey);
      writeIndex(storage, compacted);
      return { status: indexWrite, operation: 'setItem:index' };
    }

    const pointerWrite = writeRaw(
      storage,
      latestPointerKey(language),
      JSON.stringify(pointer),
    );
    if (pointerWrite !== 'stored') {
      if (!existingEntry.value) removeBestEffort(storage, entryKey);
      writeIndex(storage, compacted);
      return { status: pointerWrite, operation: 'setItem:pointer' };
    }

    const postCommitProtected = new Set<string>([entryKey]);
    if (activeEntryKey) postCommitProtected.add(activeEntryKey);
    const pruned = compactEntries(storage, nextEntries, 0, postCommitProtected, false);
    if (pruned) writeIndex(storage, pruned);

    return { status: 'stored', checksum, bytes };
  };

  const protect = (language: LanguageCode | null, version: string | null): void => {
    protectedIdentity = language && version ? { language, version } : null;
  };

  const clear = (): LocaleCacheClearResult => {
    const storage = acquireStorage();
    if (!storage) return { status: 'unavailable', operation: 'acquire' };

    const keys: string[] = [];
    try {
      for (let index = 0; index < storage.length; index += 1) {
        const key = storage.key(index);
        if (key?.startsWith(CACHE_PREFIX)) keys.push(key);
      }
    } catch {
      return { status: 'unavailable', operation: 'enumerate' };
    }

    let removed = 0;
    for (const key of keys) {
      try {
        storage.removeItem(key);
        removed += 1;
      } catch {
        return { status: 'unavailable', operation: 'removeItem' };
      }
    }
    protectedIdentity = null;
    return { status: 'stored', removed };
  };

  return { read, write, protect, clear };
}

export const localeCache = createLocaleCacheAdapter(() => window.sessionStorage);
